import { BottomSheetModal, useModal } from "@becon-team/becon-fe-lib/my-modal";
import { filterNonNullish, getArrLength } from "@becon-team/becon-fe-lib/utils";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import type { Nullish, Undefinable } from "@becon-team/becon-fe-lib/my-types";
import { modalIds } from "libs/my-modal";
import { openBrowser } from "utils/openBrowser";
import { GoogleIcon, LocationOutline } from "@becon-team/becon-fe-lib/my-svgs";
import { KakaoChannel, NaverPlace } from "@becon-team/becon-fe-lib/components";

type ModalParams = {
  name: string;
  address: Nullish<string>;
  reservations: { kakaoUrl: Nullish<string>; naverUrl: Nullish<string>; webSiteUrl: Nullish<string> };
};

export const PartnerCompanyContactModal = () => {
  const modalId = modalIds["partner-company-contact"];
  const { t } = useTranslation();

  const { modal, closeModal } = useModal(modalId);
  const { isOpened, params } = modal;
  const { name = "", address, reservations } = (params as Undefinable<ModalParams>) || {};

  // * 입력된 값이 없을 때 빈문자열
  const haveReservationInfo =
    getArrLength(filterNonNullish(Object.values(reservations || {})).filter((url) => url)) > 0;

  return (
    <BottomSheetModal closeModal={closeModal} titleSection={name} isOpened={isOpened} isCloseModalOutsideClick={true}>
      <div className="px-inner-padding pb-sp-24">
        <div className="mb-sp-24 flex items-center gap-sp-4">
          <LocationOutline className="w-fs-sm stroke-base-600" />
          <div className="text-sm text-base-600">{address || t("위치 정보 없음")}</div>
        </div>

        {(() => {
          if (haveReservationInfo) {
            return (
              <div className="space-y-sp-12">
                {reservations?.kakaoUrl && (
                  <button
                    className="my-btn-kakao w-full"
                    onClick={() => reservations?.kakaoUrl && openBrowser(reservations.kakaoUrl)}
                  >
                    <div className="flex items-center justify-center">
                      <KakaoChannel className="w-fs-2xl" />
                      {t("카카오채널")}
                    </div>
                  </button>
                )}
                {reservations?.naverUrl && (
                  <button
                    className="my-btn-naver w-full"
                    onClick={() => reservations?.naverUrl && openBrowser(reservations.naverUrl)}
                  >
                    <div className="flex items-center justify-center">
                      <NaverPlace className="w-fs-2xl" />
                      {t("네이버 예약")}
                    </div>
                  </button>
                )}
                {reservations?.webSiteUrl && (
                  <button
                    className="my-btn-base w-full"
                    onClick={() => reservations?.webSiteUrl && openBrowser(reservations.webSiteUrl)}
                  >
                    {t("홈페이지")}
                  </button>
                )}
              </div>
            );
          } else {
            return (
              <button
                className="my-btn-base w-full"
                onClick={() => openBrowser(`https://www.google.com/search?q=${name}`)}
              >
                <div className="flex items-center justify-center gap-sp-4">
                  <GoogleIcon />
                  {t("구글 검색")}
                </div>
              </button>
            );
          }
        })()}
      </div>
    </BottomSheetModal>
  );
};

export const usePartnerCompanyContactModal = () => {
  const { modal, openModal, closeModal } = useModal(modalIds["partner-company-contact"]);

  const openModalCallback = useCallback(
    (params: ModalParams) => {
      openModal(params);
    },
    [openModal],
  );

  return {
    openModal: openModalCallback,
    closeModal,
    isOpened: modal.isOpened,
  };
};
